import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoginResponse } from "subModule/src/services/userProfile/interface";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { useGetSOSDataService } from "subModule/src/services/analytics/useAnalytics";
import { PieChart } from "@mui/x-charts/PieChart";
import { InfiniteData } from "@tanstack/react-query";
import { GetStationDropdownResponse } from "subModule/src/services/station/interface";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  dashboardWidgetTitleSx,
  dashboardWidgetIconSx,
  dashboardWidgetTitleRowSx,
} from "components/Analytics/dashboardWidgetLayout";

interface SoSReportProps {
  user: LoginResponse | undefined;
  themes: any;
  stations: { id: number; name: string }[] | [];
  enqueueSnackbar: EnqueueSnackbar;
  stationsLoaded: boolean;
  handleStationActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  setPageNo: any;
  stationsData: InfiniteData<GetStationDropdownResponse, unknown> | undefined;
  searchLoading: string | null;
  pageNo: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}

const compactSelectSx = {
  "& .css-7896aj-control, & .css-1xiok1u-control": {
    minHeight: "auto",
    fontSize: '9px',
    height: "auto",
    overflow: "hidden",
    "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
      {
        background: "#fff",
        padding: 0,
        width: "15px",
        height: "15px",
      },
    "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator": {
      position: "absolute",
      right: 0,
    },
    border: 0,
  },
};

export default function SoSReport(props: SoSReportProps) {
  const { t, i18n } = useTranslation();
  const firstRender = useRef(true);
  const [data, setData] = useState<any>(null);
  const screenWidth = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1400px)",
  );
  const [loading, setLoading] = useState(true);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: "monthly", label: t("mtd") },
      allStation: false,
    },
  });
  const filters = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  const values = watch(["stationId", "range", "allStation"]);
  const primary = props.themes?.primary || "#7C3AED";
  const secondary = props.themes?.secondary || "#F97316";
  const recoveredColor = "#22C55E";

  const { refetch: apiRefetch } = useGetSOSDataService({
    stationId: values[0]?.value || null,
    range: values[1]?.value,
    allStation: values[2],
  });

  useEffect(() => {
    if (firstRender.current && props.stationsLoaded === true) {
      firstRender.current = false;
      setValue(
        "stationId",
        (props?.stations?.length > 1
          ? props?.stations?.[1]
          : props?.stations?.[0]) || null,
      );
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stations]);

  useEffect(() => {
    const currentRange = watch("range");
    const updated = filters.find((f) => f.value === currentRange?.value);
    if (updated) setValue("range", updated);
  }, [i18n.language, filters]);

  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("stationId", props.globalFilters.stationId);
      setValue("range", props.globalFilters.range);
      setValue("allStation", props.globalFilters.allStation);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);

  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: any) => {
        if (res?.data?.success) {
          setData(res?.data?.data || {});
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => console.error("===", err))
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };

  const pending = Number(data?.pending || 0);
  const dispatched = Number(data?.dispatch || 0);
  const recovered = Number(data?.recovered || 0);
  const total = pending + dispatched + recovered;
  const allZero = total === 0;

  const chartData = [
    {
      id: 0,
      label: t("pending"),
      value: allZero ? 1 : Math.abs(pending),
      color: secondary,
    },
    {
      id: 1,
      label: t("dispatched"),
      value: allZero ? 1 : Math.abs(dispatched),
      color: primary,
    },
    {
      id: 2,
      label: t("recovered"),
      value: allZero ? 1 : Math.abs(recovered),
      color: recoveredColor,
    },
  ];

  const legend = [
    { label: t("pending"), value: pending, color: secondary },
    { label: t("dispatched"), value: dispatched, color: primary },
    { label: t("recovered"), value: recovered, color: recoveredColor },
  ];

  return (
    <Card
      sx={{
        height: "100%",
        minHeight: 270,
        boxShadow: "0 1px 3px rgba(16, 24, 40, 0.06)",
        borderRadius: "12px",
        border: "1px solid #EEF0F3",
        "& .MuiCardContent-root": {
          padding: "16px !important",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <CardContent>
        <Box
          sx={{ ...dashboardWidgetHeaderSx, flexShrink: 0, mb: 1.5 }}
        >
          <Box sx={dashboardWidgetTitleRowSx}>
            <Box
              sx={{
                ...dashboardWidgetIconSx,
                width: 36,
                height: 36,
                borderRadius: "10px",
                backgroundColor: `${primary}1F`,
                color: primary,
                
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                "& svg": { fontSize: 17, color: primary },
              }}
            >
              <WarningAmberOutlinedIcon />
            </Box>
            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "2px",
              }}
            >
              <Typography
                className="font-Saira text-left"
                sx={{
                  fontSize: { xs: 12, sm: 15 },
              fontWeight: { xs: 600, sm: 700 },
              textAlign: "left",
                  color: "#0F2744",
                  lineHeight: 1.25,
                }}
              >
                {t("sosReport")}
              </Typography>
              <Typography
                className="font-Saira text-left"
                sx={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: "#9CA3AF",
                  lineHeight: 1.3,
                }}
              >
                {t("vehicleIncidentSummary")}
              </Typography>
            </Box>
          </Box>

          <Box className="flex" sx={{ flexShrink: 0 }}>
            <Controller
              name="stationId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box sx={compactSelectSx}>
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={props?.stations || []}
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      if (selectedOption?.value === 0)
                        setValue("allStation", true);
                      else setValue("allStation", false);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.stationId?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("station")}
                    isLoading={props.searchLoading === "sosReport"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        props.handleStationActions(
                          "sosReport",
                          "searchIt",
                          search,
                        );
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (props.dropdownSearchTerm || props.pageNo > 1)
                          props.handleStationActions(
                            "sosReport",
                            "searchIt",
                            "",
                          );
                        else props.setDropdownSearchTerm("");
                        props.setPageNo(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(props.stationsData?.pages[0]?.data as any).last)
                        props.handleStationActions("sosReport", "loadNext");
                    }}
                    page={props.pageNo}
                  />
                </Box>
              )}
            />
            <Controller
              name="range"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    ...compactSelectSx,
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      ...compactSelectSx[
                        "& .css-7896aj-control, & .css-1xiok1u-control"
                      ],
                      width: screenWidth ? "60px" : "auto",
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={filters}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.range?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    isDisabled={loading}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>

        {loading ? (
          <Box
            className="flex flex-col sm:flex-row items-center gap-3"
            sx={{ flex: 1, width: "100%" }}
          >
            <Skeleton
              variant="circular"
              width={120}
              height={120}
              sx={{ backgroundColor: secondary, flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, width: "100%" }}>
              <Skeleton sx={{ backgroundColor: secondary, mb: 1 }} />
              <Skeleton sx={{ backgroundColor: secondary, mb: 1 }} />
              <Skeleton sx={{ backgroundColor: secondary }} />
            </Box>
          </Box>
        ) : (
          <Box
            className="flex flex-col sm:flex-row items-center"
            sx={{
              flex: 1,
              minHeight: 0,
              width: "100%",
              gap: { xs: 2, sm: 3 },
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 120,
                height: 120,
                flexShrink: 0,
              }}
            >
              <PieChart
                height={120}
                width={120}
                series={[
                  {
                    data: chartData,
                    valueFormatter: (v) =>
                      `${allZero ? 0 : v.value?.toLocaleString() || 0}`,
                    innerRadius: 36,
                    outerRadius: 54,
                    cx: 60,
                    cy: 60,
                    paddingAngle: 2,
                    cornerRadius: 4,
                  },
                ]}
                slotProps={{ legend: { hidden: true } }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                }}
              >
                <Typography
                  className="font-Saira"
                  sx={{ fontSize: 10, fontWeight: 600, color: "#9CA3AF" }}
                >
                  {t("total")}
                </Typography>
                <Typography
                  className="font-Saira"
                  sx={{
                    fontSize: 21,
                    fontWeight: 800,
                    color: "#0F2744",
                    lineHeight: 1,
                  }}
                >
                  {total.toLocaleString()}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                width: "100%",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                justifyContent: "center",
              }}
            >
              {legend.map((item) => (
                <Tooltip
                  key={item.label}
                  title={item.value.toLocaleString()}
                  enterDelay={0}
                >
                  <Box
                    className="flex items-center justify-between gap-2"
                    sx={{ width: "100%" }}
                  >
                    <Box sx={dashboardWidgetTitleRowSx}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: item.color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        className="font-Saira"
                        sx={{
                          fontSize: 12,
                          color: "#6B7280",
                          fontWeight: 500,
                          lineHeight: 1.2,
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                    <Typography
                      className="font-Saira"
                      sx={{
                        fontSize: 15,
                        fontWeight: 800,
                        color: item.color,
                        lineHeight: 1.2,
                        flexShrink: 0,
                      }}
                    >
                      {item.value.toLocaleString()}
                    </Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
